import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from 'react-oidc-context';
import InsuranceService from '../../services/insuranceService';
import Button from '../common/Button';
import './Insurances.css';

const Insurances = () => {
  const auth = useAuth();
  const [insurances, setInsurances] = useState([]);
  const [filteredInsurances, setFilteredInsurances] = useState([]);
  const [searchFilter, setSearchFilter] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [showModal, setShowModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [editingInsurance, setEditingInsurance] = useState(null);
  const [deletingInsurance, setDeletingInsurance] = useState(null);
  const [formData, setFormData] = useState({
    name: ''
  });

  // Load insurances from API
  const loadInsurances = useCallback(async () => {
    try {
      setInitialLoading(true);
      const token = auth.user?.access_token;
      const response = await InsuranceService.getAllInsurances(token);
      setInsurances(response.insurances || []);
    } catch (error) {
      console.error('Error loading insurances:', error);
      setMessage({
        type: 'error',
        text: 'Error al cargar las aseguradoras'
      });
    } finally {
      setInitialLoading(false);
    }
  }, [auth.user?.access_token]);

  // Load insurances on component mount
  useEffect(() => {
    loadInsurances();
  }, [loadInsurances]);

  // Filter insurances when search filter or insurances change
  useEffect(() => {
    if (!searchFilter.trim()) {
      setFilteredInsurances(insurances);
    } else {
      // For API-based search, we can implement real-time search
      const filtered = insurances.filter(insurance =>
        insurance.name.toLowerCase().includes(searchFilter.toLowerCase())
      );
      setFilteredInsurances(filtered);
    }
  }, [searchFilter, insurances]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSearchChange = (e) => {
    setSearchFilter(e.target.value);
  };

  const clearSearch = () => {
    setSearchFilter('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setMessage({ type: '', text: '' });

    try {
      const token = auth.user?.access_token;
      
      if (editingInsurance) {
        // Update existing insurance
        const response = await InsuranceService.updateInsurance(editingInsurance.id, formData, token);
        const updatedInsurances = insurances.map(insurance =>
          insurance.id === editingInsurance.id
            ? response
            : insurance
        );
        setInsurances(updatedInsurances);
        setMessage({
          type: 'success',
          text: 'Aseguradora actualizada exitosamente'
        });
      } else {
        // Create new insurance
        const response = await InsuranceService.createInsurance(formData, token);
        setInsurances(prev => [...prev, response]);
        setMessage({
          type: 'success',
          text: 'Aseguradora creada exitosamente'
        });
      }

      handleCloseModal();
    } catch (error) {
      console.error('Error submitting insurance:', error);
      setMessage({
        type: 'error',
        text: editingInsurance 
          ? 'Error al actualizar la aseguradora' 
          : 'Error al crear la aseguradora'
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleEdit = (insurance) => {
    setEditingInsurance(insurance);
    setFormData({
      name: insurance.name || ''
    });
    setShowModal(true);
  };

  const handleDelete = (insurance) => {
    setDeletingInsurance(insurance);
    setShowDeleteModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingInsurance(null);
    setFormData({
      name: ''
    });
    setMessage({ type: '', text: '' });
  };

  const handleCloseDeleteModal = () => {
    setShowDeleteModal(false);
    setDeletingInsurance(null);
    setMessage({ type: '', text: '' });
  };

  const confirmDelete = async () => {
    if (!deletingInsurance) return;

    setIsLoading(true);
    setMessage({ type: '', text: '' });

    try {
      const token = auth.user?.access_token;
      await InsuranceService.deleteInsurance(deletingInsurance.id, token);
      
      const updatedInsurances = insurances.filter(insurance => insurance.id !== deletingInsurance.id);
      setInsurances(updatedInsurances);
      
      setMessage({
        type: 'success',
        text: 'Aseguradora eliminada exitosamente'
      });

      handleCloseDeleteModal();
    } catch (error) {
      console.error('Error deleting insurance:', error);
      setMessage({
        type: 'error',
        text: 'Error al eliminar la aseguradora'
      });
    } finally {
      setIsLoading(false);
    }
  };

  if (initialLoading) {
    return (
      <div className="insurances-container">
        <div className="insurances-loading">
          <div className="loading-spinner"></div>
          <p>Cargando aseguradoras...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="insurances-container">
      {/* Header */}
      <div className="insurances-header">
        <div className="header-content">
          <h1>Aseguradoras</h1>
          <p>Administra las compañías de seguros médicos</p>
        </div>
        <button
          className="btn-primary add-btn"
          onClick={() => setShowModal(true)}
        >
          Agregar Aseguradora
        </button>
      </div>

      {/* Message */}
      {message.text && (
        <div className={`message ${message.type}`}>
          {message.text}
        </div>
      )}

      {/* Filters */}
      <div className="insurances-filters">
        <div className="search-container">
          <input
            type="text"
            placeholder="Buscar por nombre de aseguradora..."
            value={searchFilter}
            onChange={handleSearchChange}
            className="search-input"
          />
          {searchFilter && (
            <button
              className="clear-search-btn"
              onClick={clearSearch}
              title="Limpiar búsqueda"
            >
              ✕
            </button>
          )}
        </div>
      </div>

      {/* Insurances Table */}
      <div className="insurances-table-container">
        {filteredInsurances.length === 0 ? (
          <div className="no-insurances">
            <p>No se encontraron aseguradoras</p>
            {searchFilter && (
              <button className="btn-secondary" onClick={clearSearch}>
                Limpiar filtros
              </button>
            )}
          </div>
        ) : (
          <>
            {/* Desktop Table */}
            <table className="insurances-table">
              <thead>
                <tr>
                  <th>Nombre</th>
                  <th>Fecha de Creación</th>
                  <th>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {filteredInsurances.map((insurance) => {
                  return (
                    <tr key={insurance.id}>
                      <td className="insurance-name">
                        <div className="insurance-details">
                          <span className="name">{insurance.name}</span>
                        </div>
                      </td>
                      <td className="insurance-date">
                        {new Date(insurance.createdAt).toLocaleDateString('es-MX')}
                      </td>
                      <td className="insurance-actions">
                        <button
                          className="btn-secondary edit-btn"
                          onClick={() => handleEdit(insurance)}
                          title="Editar aseguradora"
                        >
                          Editar
                        </button>
                        <button
                          className="btn-danger delete-btn"
                          onClick={() => handleDelete(insurance)}
                          title="Eliminar aseguradora"
                        >
                          Eliminar
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>

            {/* Mobile Cards */}
            <div className="insurances-mobile-cards">
              {filteredInsurances.map((insurance) => (
                <div key={insurance.id} className="insurance-card">
                  <div className="insurance-card-header">
                    <div className="insurance-card-title">{insurance.name}</div>
                  </div>
                  <div className="insurance-card-content">
                    <div className="insurance-card-row">
                      <span className="insurance-card-label">Creado:</span>
                      <span className="insurance-card-value">
                        {new Date(insurance.createdAt).toLocaleDateString('es-MX')}
                      </span>
                    </div>
                  </div>
                  <div className="insurance-card-actions">
                    <button
                      className="btn-secondary edit-btn"
                      onClick={() => handleEdit(insurance)}
                      title="Editar aseguradora"
                    >
                      Editar
                    </button>
                    <button
                      className="btn-danger delete-btn"
                      onClick={() => handleDelete(insurance)}
                      title="Eliminar aseguradora"
                    >
                      Eliminar
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </div>

      {/* Add/Edit Modal */}
      {showModal && (
        <div className="modal-overlay">
          <div className="modal">
            <div className="modal-header">
              <h3>{editingInsurance ? 'Editar Aseguradora' : 'Agregar Nueva Aseguradora'}</h3>
              <button className="modal-close" onClick={handleCloseModal}>
                ✕
              </button>
            </div>
            
            <form onSubmit={handleSubmit} className="modal-form">
              <div className="form-grid">
                <div className="form-group">
                  <label htmlFor="name">
                    Nombre de la Aseguradora <span className="required">*</span>
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    required
                    placeholder="Ej: Seguros Universales, MetLife, etc."
                  />
                </div>
              </div>

              <div className="modal-actions">
                <button
                  type="button"
                  className="btn-secondary"
                  onClick={handleCloseModal}
                  disabled={isLoading}
                >
                  Cancelar
                </button>
                <Button
                  type="submit"
                  variant="primary"
                  loading={isLoading}
                  icon="fas fa-save"
                >
                  {isLoading
                    ? (editingInsurance ? 'Actualizando...' : 'Creando...')
                    : (editingInsurance ? 'Actualizar' : 'Crear')
                  }
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div className="modal-overlay">
          <div className="modal delete-modal">
            <div className="modal-header">
              <h3>Confirmar Eliminación</h3>
              <button className="modal-close" onClick={handleCloseDeleteModal}>
                ✕
              </button>
            </div>
            
            <div className="modal-content">
              <p>
                ¿Estás seguro de que deseas eliminar la aseguradora{' '}
                <strong>"{deletingInsurance?.name}"</strong>?
              </p>
              <p className="warning-text">
                Esta acción no se puede deshacer.
              </p>
            </div>

            <div className="modal-actions">
              <button
                type="button"
                className="btn-secondary"
                onClick={handleCloseDeleteModal}
                disabled={isLoading}
              >
                Cancelar
              </button>
              <button
                type="button"
                className="btn-danger"
                onClick={confirmDelete}
                disabled={isLoading}
              >
                {isLoading ? 'Eliminando...' : 'Eliminar'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Insurances;